import React, { PropsWithChildren, createContext, useCallback, useState, useEffect } from 'react';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import {
  addTransaction,
  getAllTransactions,
  removeTransaction,
  clearTransactions,
} from '../lib/offline-queue';
import type { QueuedOrder } from '../lib/db';
import axios from '../lib/axios';
import { getUser } from '../utils/auth';

/**
 * Context type for offline functionality
 */
type OfflineContextType = {
  /** True if the device is offline (no network connection) */
  isOffline: boolean;
  /** True if the device is online (has network connection) */
  isOnline: boolean;
  /** Queue a transaction for offline sync */
  queueTransaction: (transaction: QueuedOrder) => Promise<void>;
  /** Get all queued transactions, optionally filtered by location */
  getQueuedTransactions: (locationId?: string) => Promise<QueuedOrder[]>;
  /** Remove a transaction from the queue by localId */
  removeQueuedTransaction: (localId: string) => Promise<void>;
  /** Clear all queued transactions */
  clearQueue: () => Promise<void>;
};

export const OfflineContext = createContext<OfflineContextType>({} as OfflineContextType);

/**
 * Provider component for offline functionality
 *
 * Provides offline status and transaction queue management to the application.
 * Uses the useOfflineStatus hook to track network connectivity and wraps
 * offline-queue functions for managing queued transactions.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <OfflineContextProvider>
 *       <YourApp />
 *     </OfflineContextProvider>
 *   );
 * }
 *
 * // In child components:
 * const { isOffline, queueTransaction } = useContext(OfflineContext);
 * ```
 */
export const OfflineContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  // Get online/offline status from hook
  const { isOffline, isOnline } = useOfflineStatus();

  // Store organization's offline transaction limit
  const [offlineLimit, setOfflineLimit] = useState<number | undefined>(undefined);

  // Fetch organization limits only when user is authenticated
  useEffect(() => {
    const user = getUser();
    // Only fetch limits if user is logged in
    if (!user) {
      return;
    }

    const fetchLimits = async () => {
      try {
        const response = await axios.get('/organizations/me/limits');
        setOfflineLimit(response.data.limits.offlineTransactionLimit);
      } catch (error) {
        console.error('Failed to fetch organization limits:', error);
        // Fail gracefully - if we can't fetch limits, don't enforce them
      }
    };

    fetchLimits();
  }, []);

  // Wrap queue functions with useCallback to prevent unnecessary re-renders
  const queueTransaction = useCallback(async (transaction: QueuedOrder): Promise<void> => {
    await addTransaction(transaction, offlineLimit);
  }, [offlineLimit]);

  const getQueuedTransactions = useCallback(async (locationId?: string): Promise<QueuedOrder[]> => {
    return await getAllTransactions(locationId);
  }, []);

  const removeQueuedTransaction = useCallback(async (localId: string): Promise<void> => {
    await removeTransaction(localId);
  }, []);

  const clearQueue = useCallback(async (): Promise<void> => {
    await clearTransactions();
  }, []);

  return (
    <OfflineContext.Provider
      value={{
        isOffline,
        isOnline,
        queueTransaction,
        getQueuedTransactions,
        removeQueuedTransaction,
        clearQueue,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
};
